import {
	type GenerateSuggestions,
	SuggestionsDataKey,
	type SuggestionsType,
} from '@kimuno/api-sdk/lib/types/requests/Ai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { generateAISuggestions } from '../../actions/aiAssistant.actions';
import { FieldName } from '../../components/AIAssistant/shared/TemplatePromptForm/formConfigs';
import type { StoreType } from '../../reducers';
import {
	getSuggestionsData,
	isSuggestionsError,
	isSuggestionsLoading,
} from '../../reducers/aiAssistant.selectors';
import { ampliTrackAISuggestionsGeneratedEvent } from '../../services/track.service';
import useAction from '../useAction';
import type { UseAISuggestionsOptions, UseAISuggestionsReturn } from './types';

/**
 * Custom hook for AI-powered input suggestions
 *
 * This hook manages the state and logic for AI-generated suggestions
 * that can be attached to any text input field.
 *
 * @example
 * const { value, onChange, suggestions, openSuggestions, selectSuggestion, isLoading } = useAISuggestions({
 *   generatorConfig: {
 *     promptType: "topic",
 *     relevantParams: ["fach", "klassenstufe"]
 *   },
 *   params: { fach: "Mathematik", klassenstufe: "5" }
 * });
 */
export function useAISuggestions({
	generatorConfig,
	params,
	initialValue = '',
	onValueChange,
}: UseAISuggestionsOptions): UseAISuggestionsReturn {
	const [value, setValue] = useState<string>(initialValue);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const requestIdRef = useRef<string>(v4());
	const requestId = requestIdRef.current;

	const generateSuggestionsAction = useAction(generateAISuggestions);

	const suggestions = useSelector((state: StoreType) => getSuggestionsData(state, requestId));
	const isLoading = useSelector((state: StoreType) => isSuggestionsLoading(state, requestId));
	const hasError = useSelector((state: StoreType) => isSuggestionsError(state, requestId));
	const error = hasError ? 'Failed to fetch suggestions' : null;

	const cacheRef = useRef<{
		params: Record<string, any>;
		requestId: string;
	} | null>(null);

	const haveParamsChanged = useCallback((): boolean => {
		if (!cacheRef.current) return true;

		return (
			cacheRef.current.requestId !== requestId ||
			generatorConfig.relevantParams.some((key) => cacheRef.current!.params[key] !== params[key])
		);
	}, [generatorConfig.relevantParams, params, requestId]);

	const fetchSuggestions = useCallback(async () => {
		const type = generatorConfig.promptType as SuggestionsType;
		const data: Partial<Record<SuggestionsDataKey, string>> = {};

		if (params[FieldName.SUBJECT]) {
			data[SuggestionsDataKey.SUBJECT] = params[FieldName.SUBJECT];
		}
		if (params[FieldName.CLASS_YEAR]) {
			data[SuggestionsDataKey.CLASS_YEAR] = params[FieldName.CLASS_YEAR];
		}
		if (params[FieldName.TOPIC]) {
			data[SuggestionsDataKey.TOPIC] = params[FieldName.TOPIC];
		}
		if (params[FieldName.LEARNING_GOAL]) {
			data[SuggestionsDataKey.LEARNING_GOAL] = params[FieldName.LEARNING_GOAL];
		}

		const payload: GenerateSuggestions = { type, data };

		await generateSuggestionsAction({ requestId, payload });

		cacheRef.current = {
			params: { ...params },
			requestId,
		};
	}, [generateSuggestionsAction, generatorConfig.promptType, params, requestId]);

	const openSuggestions = useCallback(() => {
		setIsOpen(true);

		if (haveParamsChanged()) {
			ampliTrackAISuggestionsGeneratedEvent(
				generatorConfig.promptType === 'topic' ? 'topic' : 'learning goal'
			);
			fetchSuggestions();
		} else if (cacheRef.current && suggestions.length > 0) {
			return;
		} else {
			fetchSuggestions();
		}
	}, [haveParamsChanged, fetchSuggestions, suggestions.length, generatorConfig.promptType]);

	const closeSuggestions = useCallback(() => {
		setIsOpen(false);
	}, []);

	const onChange = useCallback(
		(newValue: string) => {
			setValue(newValue);
			if (onValueChange) {
				onValueChange(newValue);
			}
		},
		[onValueChange]
	);

	const selectSuggestion = useCallback(
		(suggestion: string) => {
			setValue(suggestion);
			if (onValueChange) {
				onValueChange(suggestion);
			}
			closeSuggestions();
		},
		[onValueChange, closeSuggestions]
	);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	return {
		value,
		onChange,
		suggestions,
		isLoading,
		isOpen,
		openSuggestions,
		closeSuggestions,
		selectSuggestion,
		error,
	};
}
